import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertCircle,
  Trash2,
  AlertTriangle,
  CreditCard,
  Save,
  Bell,
  Shield,
  Users,
  Send
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import BusinessIntelligenceDashboard from '../../components/coaching/BusinessIntelligenceDashboard';
import SendNotificationModal from '../../components/coaching/SendNotificationModal';
import { 
  getAllExamData,
  deleteCoachingAccount,
  updateUserProfile
} from '../../firebase/collections';
import { signOut, deleteUser } from 'firebase/auth';
import { auth } from '../../firebase/config';
import toast from 'react-hot-toast';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { checkSubscriptionLimit, getStudentRangeForCount, STUDENT_RANGES } from '../../utils/subscriptionPricing';

const CoachingAdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useRealtime();
  
  // Permission check for staff
  const isStaff = userProfile?.role === 'coaching_staff';
  const hasDashboardAccess = !isStaff || userProfile?.accessibleSections?.includes('dashboard');

  const [studentCount, setStudentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLimitStatus, setSubscriptionLimitStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    email: '',
    phone: '',
    businessType: 'individual',
    businessName: '',
    pan: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    loadDashboardData();
    if (userProfile?.subscription) {
      setSubscriptionStatus(userProfile.subscription);
    }
    // Load bank details if they exist
    if (userProfile?.bankDetails) {
      setBankDetails({
        accountHolderName: userProfile.bankDetails.accountHolderName || '',
        accountNumber: userProfile.bankDetails.accountNumber || '',
        ifscCode: userProfile.bankDetails.ifscCode || '',
        bankName: userProfile.bankDetails.bankName || '',
        email: userProfile.bankDetails.email || userProfile?.email || '',
        phone: userProfile.bankDetails.phone || userProfile?.phone || userProfile?.contact?.phone || '',
        businessType: userProfile.bankDetails.businessType || 'individual',
        businessName: userProfile.bankDetails.businessName || userProfile?.institutionName || userProfile?.coachingName || '',
        pan: userProfile.bankDetails.pan || userProfile?.pan || '',
        street1: userProfile.bankDetails.street1 || userProfile?.address?.street || '',
        street2: userProfile.bankDetails.street2 || userProfile?.address?.street2 || '',
        city: userProfile.bankDetails.city || userProfile?.address?.city || '',
        state: userProfile.bankDetails.state || userProfile?.address?.state || '',
        pincode: userProfile.bankDetails.pincode || userProfile?.address?.pincode || ''
      });
    } else if (userProfile) {
      setBankDetails(prev => ({
        ...prev,
        email: userProfile.email || '',
        phone: userProfile.phone || userProfile.contact?.phone || '',
        businessName: userProfile.institutionName || userProfile.coachingName || '',
        pan: userProfile.pan || '',
        street1: userProfile.address?.street || '',
        city: userProfile.address?.city || '',
        state: userProfile.address?.state || '',
        pincode: userProfile.address?.pincode || ''
      }));
    }

    // Set up real-time listener for student count
    const isSuperAdmin = userProfile?.role === 'admin' || 
                         userProfile?.role === 'superadmin' || 
                         userProfile?.role === 'founder';
    const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

    if (coachingId) {
      const studentsQuery = query(
        collection(db, 'students'),
        where('coachingId', '==', coachingId)
      );
      
      const unsubscribe = onSnapshot(studentsQuery, (snapshot) => {
        setStudentCount(snapshot.size);
      }, (error) => {
        console.error('Error listening to students:', error);
      });

      return () => unsubscribe();
    }
  }, [userProfile]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Check if user is a super admin (not coaching admin)
      const isSuperAdmin = userProfile?.role === 'admin' || 
                           userProfile?.role === 'superadmin' || 
                           userProfile?.role === 'founder';
      
      // Only use coachingId if user is a coaching admin or staff, not a super admin
      const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

      // Count total students from students collection (much simpler!)
      let totalStudents = 0;
      if (coachingId) {
        try {
          const studentsQuery = query(
            collection(db, 'students'),
            where('coachingId', '==', coachingId)
          );
          const studentsSnapshot = await getDocs(studentsQuery);
          totalStudents = studentsSnapshot.size;
        } catch (error) {
          console.error('Error counting students:', error);
        }
      }

      setStudentCount(totalStudents);

      // Check subscription limit if user has category and studentRange
      if (userProfile?.category && userProfile?.studentRange && totalStudents > 0) {
        const limitStatus = checkSubscriptionLimit(totalStudents, userProfile.studentRange);
        setSubscriptionLimitStatus(limitStatus);

        // Auto-update subscription range if exceeded
        if (limitStatus.isExceeded && limitStatus.nextRange !== userProfile.studentRange) {
          const newRange = limitStatus.nextRange;
          try {
            await updateUserProfile(currentUser.uid, {
              studentRange: newRange
            });
            toast.success(`Your subscription has been automatically updated to ${STUDENT_RANGES.find(r => r.value === newRange)?.label || newRange} based on your current student count.`);
            // Refresh user profile to get updated data
            if (refreshUserProfile) {
              await refreshUserProfile();
            }
          } catch (error) {
            console.error('Error updating subscription range:', error);
            toast.error('Failed to update subscription range automatically. Please update manually.');
          }
        }
      }
    } catch (error) {
      // Error loading dashboard data
    } finally {
      setLoading(false);
    }
  };


  const getSubscriptionStatus = () => {
    if (!subscriptionStatus) return null;
    
    if (subscriptionStatus.status === 'active') {
      const endDate = subscriptionStatus.endDate?.toDate?.() || new Date(subscriptionStatus.endDate);
      const daysRemaining = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
      
      return {
        status: 'active',
        daysRemaining,
        endDate: endDate.toLocaleDateString(),
        message: daysRemaining > 0 
          ? `Active - ${daysRemaining} days remaining`
          : 'Expiring soon'
      };
    }
    
    return {
      status: subscriptionStatus.status,
      message: subscriptionStatus.status === 'pending' ? 'Payment Pending' : 'Inactive'
    };
  };

  const subStatus = getSubscriptionStatus();

  const handleDeleteAccount = async () => {
    if (!currentUser || !userProfile?.coachingId) {
      toast.error('Unable to delete account. Missing user information.');
      return;
    }

    setDeleting(true);
    try {
      // Delete coaching account and associated data (this deletes the user profile)
      const result = await deleteCoachingAccount(currentUser.uid, userProfile.coachingId);
      
      if (result.success) {
        // Try to delete Firebase Auth user before signing out
        // (may fail if recent auth required, but profile is already deleted)
        try {
          await deleteUser(currentUser);
        } catch (authError) {
          // Profile is already deleted, so account is effectively deleted
          // Auth user deletion may require recent authentication, but that's okay
        }

        // Sign out and redirect to home page immediately
        await signOut(auth);
        toast.success('Account deleted successfully');
        navigate('/');
      } else {
        toast.error('Failed to delete account: ' + (result.error || 'Unknown error'));
        setDeleting(false);
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      toast.error('Error deleting account: ' + error.message);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!currentUser) {
      toast.error('User not authenticated');
      return;
    }

    // Validate required fields
    if (!bankDetails.accountHolderName.trim() || !bankDetails.accountNumber.trim() || 
        !bankDetails.ifscCode.trim() || !bankDetails.bankName.trim() ||
        !bankDetails.email.trim() || !bankDetails.phone.trim() ||
        !bankDetails.businessName.trim() || !bankDetails.street1.trim() ||
        !bankDetails.city.trim() || !bankDetails.state.trim() || !bankDetails.pincode.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate IFSC code format (11 characters, alphanumeric)
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifscCode.toUpperCase())) {
      toast.error('Invalid IFSC code format');
      return;
    }

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bankDetails.email)) {
      toast.error('Invalid email format');
      return;
    }

    // Validate phone (10 digits)
    if (!/^\d{10}$/.test(bankDetails.phone)) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    // Validate pincode (6 digits)
    if (!/^\d{6}$/.test(bankDetails.pincode)) {
      toast.error('Pincode must be 6 digits');
      return;
    }

    // Validate PAN (10 characters: 5 letters, 4 digits, 1 letter)
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(bankDetails.pan.toUpperCase())) {
      toast.error('Invalid PAN format (e.g., ABCDE1234F)');
      return;
    }

    setSavingBankDetails(true);
    try {
      // First save bank details to user profile
      const result = await updateUserProfile(currentUser.uid, {
        bankDetails: {
          accountHolderName: bankDetails.accountHolderName.trim(),
          accountNumber: bankDetails.accountNumber.trim(),
          ifscCode: bankDetails.ifscCode.toUpperCase().trim(),
          bankName: bankDetails.bankName.trim(),
          email: bankDetails.email.trim(),
          phone: bankDetails.phone.trim(),
          businessType: bankDetails.businessType,
          businessName: bankDetails.businessName.trim(),
          pan: bankDetails.pan.toUpperCase().trim(),
          street1: bankDetails.street1.trim(),
          street2: bankDetails.street2.trim(),
          city: bankDetails.city.trim(),
          state: bankDetails.state.trim(),
          pincode: bankDetails.pincode.trim()
        }
      });

      if (result.success) {
        // Now create Razorpay linked account for Route payments
        try {
          const { createLinkedAccount } = await import('../../services/razorpayRouteService');
          const linkedAccountResult = await createLinkedAccount({
            accountHolderName: bankDetails.accountHolderName.trim(),
            accountNumber: bankDetails.accountNumber.trim(),
            ifscCode: bankDetails.ifscCode.toUpperCase().trim(),
            bankName: bankDetails.bankName.trim(),
            email: bankDetails.email.trim(),
            phone: bankDetails.phone.trim(),
            businessType: bankDetails.businessType,
            businessName: bankDetails.businessName.trim(),
            legalBusinessName: bankDetails.businessName.trim(),
            contactName: bankDetails.accountHolderName.trim(),
            pan: bankDetails.pan.toUpperCase().trim(),
            street1: bankDetails.street1.trim(),
            street2: bankDetails.street2.trim() || 'NA',
            city: bankDetails.city.trim(),
            state: bankDetails.state.trim(),
            pincode: bankDetails.pincode.trim()
          });

          if (linkedAccountResult.success) {
            toast.success('Bank account linked for receiving payments!');
          } else {
            console.error('Linked account error:', linkedAccountResult.error);
            toast.error('Bank details saved but payout setup failed: ' + (linkedAccountResult.error || 'Unknown error'));
          }
        } catch (linkedError) {
          console.error('Linked account creation error:', linkedError);
          toast.error('Bank details saved but payout setup failed: ' + (linkedError.message || 'Unknown error'));
        }
        
        setShowBankDetails(false);
        refreshUserProfile?.();
      } else {
        toast.error('Failed to save bank details');
      }
    } catch (error) {
      toast.error('Error saving bank details: ' + error.message);
    } finally {
      setSavingBankDetails(false);
    }
  };

  if (!hasDashboardAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6">
          <Shield className="text-red-600 dark:text-red-400" size={40} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          You do not have permission to view the Dashboard. Please contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welcome, {userProfile?.contactPerson || 'Admin'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {userProfile?.coachingName || 'School/Coaching Institute'} - Admin Dashboard
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowNotificationModal(true)}
            className="flex items-center justify-center space-x-2 px-5 py-3 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-xl shadow-lg hover:shadow-green-500/30 transition-all font-bold"
          >
            <Send className="w-5 h-5" />
            <span>Send Notification</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(`/coaching/${userProfile?.coachingId || 'default'}/actions`)}
            className="flex items-center justify-center space-x-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-blue-500/30 transition-all font-bold"
          >
            <Bell className="w-5 h-5 animate-bounce" />
            <span>Open Action Center</span>
          </motion.button>
        </div>
      </div>

      {/* Subscription Status */}
      {subStatus && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${
            subStatus.status === 'active'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {subStatus.status === 'active' ? (
                <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
              ) : (
                <AlertCircle className="text-yellow-600 dark:text-yellow-400" size={24} />
              )}
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  Subscription: {subStatus.message}
                </p>
                {subStatus.endDate && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Valid until: {subStatus.endDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Subscription Limit Warning */}
      {subscriptionLimitStatus && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${
            subscriptionLimitStatus.isExceeded
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : subscriptionLimitStatus.isApproaching
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}
        >
          <div className="flex items-start space-x-3">
            {subscriptionLimitStatus.isExceeded ? (
              <AlertTriangle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={24} />
            ) : subscriptionLimitStatus.isApproaching ? (
              <AlertCircle className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" size={24} />
            ) : (
              <CheckCircle className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={24} />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${
                subscriptionLimitStatus.isExceeded
                  ? 'text-red-900 dark:text-red-200'
                  : subscriptionLimitStatus.isApproaching
                  ? 'text-orange-900 dark:text-orange-200'
                  : 'text-blue-900 dark:text-blue-200'
              }`}>
                {subscriptionLimitStatus.isExceeded
                  ? 'Subscription Limit Exceeded'
                  : subscriptionLimitStatus.isApproaching
                  ? 'Approaching Subscription Limit'
                  : 'Student Count Status'}
              </p>
              <p className={`text-sm mt-1 ${
                subscriptionLimitStatus.isExceeded
                  ? 'text-red-700 dark:text-red-300'
                  : subscriptionLimitStatus.isApproaching
                  ? 'text-orange-700 dark:text-orange-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                {subscriptionLimitStatus.isExceeded ? (
                  <>
                    You currently have <strong>{subscriptionLimitStatus.studentCount.toLocaleString()} students</strong>, which exceeds your subscription limit of{' '}
                    <strong>{STUDENT_RANGES.find(r => r.value === subscriptionLimitStatus.currentRange)?.label || subscriptionLimitStatus.currentRange}</strong>.
                    {subscriptionLimitStatus.nextRange !== subscriptionLimitStatus.currentRange && (
                      <> Your subscription has been automatically updated to{' '}
                      <strong>{STUDENT_RANGES.find(r => r.value === subscriptionLimitStatus.nextRange)?.label || subscriptionLimitStatus.nextRange}</strong>.
                      The new pricing will apply on your next renewal.</>
                    )}
                  </>
                ) : subscriptionLimitStatus.isApproaching ? (
                  <>
                    You currently have <strong>{subscriptionLimitStatus.studentCount.toLocaleString()} students</strong> out of your subscription limit of{' '}
                    <strong>{STUDENT_RANGES.find(r => r.value === subscriptionLimitStatus.currentRange)?.label || subscriptionLimitStatus.currentRange}</strong>.
                    You're approaching the limit. If you exceed it, your subscription price will automatically update to the next tier on renewal.
                  </>
                ) : (
                  <>
                    You currently have <strong>{subscriptionLimitStatus.studentCount.toLocaleString()} students</strong> within your subscription limit of{' '}
                    <strong>{STUDENT_RANGES.find(r => r.value === subscriptionLimitStatus.currentRange)?.label || subscriptionLimitStatus.currentRange}</strong>.
                  </>
                )}
              </p>
              {subscriptionLimitStatus.isApproaching && (
                <button
                  onClick={() => navigate('/coaching-admin/subscription')}
                  className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                >
                  View Subscription Plans
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}




      {/* Bank Details Section */}
      {userProfile?.role !== 'coaching_staff' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <CreditCard className="text-blue-600 dark:text-blue-400" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bank Account Details
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enter your bank details to receive payments
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowBankDetails(!showBankDetails)}
              className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              {showBankDetails ? 'Hide' : bankDetails.accountNumber ? 'Update' : 'Add Details'}
            </button>
          </div>

          {showBankDetails && (
            <div className="mt-6 space-y-4">
              <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Bank Account Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Account Holder Name *
                    </label>
                  <input
                    type="text"
                    value={bankDetails.accountHolderName}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter account holder name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter account number"
                    maxLength={20}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IFSC Code *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.ifscCode}
                    onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="ABCD0123456"
                    maxLength={11}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.bankName}
                    onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter bank name"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Contact Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={bankDetails.email}
                    onChange={(e) => setBankDetails({ ...bankDetails, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={bankDetails.phone}
                    onChange={(e) => setBankDetails({ ...bankDetails, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter 10-digit phone"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

            {/* Business Details Section */}
            <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Business Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Business Type *
                  </label>
                  <select
                    value={bankDetails.businessType}
                    onChange={(e) => setBankDetails({ ...bankDetails, businessType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="individual">Individual</option>
                    <option value="proprietorship">Proprietorship</option>
                    <option value="partnership">Partnership</option>
                    <option value="private_limited">Private Limited</option>
                    <option value="public_limited">Public Limited</option>
                    <option value="llp">LLP</option>
                    <option value="trust">Trust</option>
                    <option value="society">Society</option>
                    <option value="ngo">NGO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Business/Institute Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.businessName}
                    onChange={(e) => setBankDetails({ ...bankDetails, businessName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter business/institute name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    PAN Number *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.pan}
                    onChange={(e) => setBankDetails({ ...bankDetails, pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="pb-4">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Address Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.street1}
                    onChange={(e) => setBankDetails({ ...bankDetails, street1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter street address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Street Address Line 2 (Optional)
                  </label>
                  <input
                    type="text"
                    value={bankDetails.street2}
                    onChange={(e) => setBankDetails({ ...bankDetails, street2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Apartment, suite, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.city}
                    onChange={(e) => setBankDetails({ ...bankDetails, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.state}
                    onChange={(e) => setBankDetails({ ...bankDetails, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter state"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    value={bankDetails.pincode}
                    onChange={(e) => setBankDetails({ ...bankDetails, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter 6-digit pincode"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowBankDetails(false);
                  // Reset to saved values
                  if (userProfile?.bankDetails) {
                    setBankDetails({
                      accountHolderName: userProfile.bankDetails.accountHolderName || '',
                      accountNumber: userProfile.bankDetails.accountNumber || '',
                      ifscCode: userProfile.bankDetails.ifscCode || '',
                      bankName: userProfile.bankDetails.bankName || '',
                      email: userProfile.bankDetails.email || userProfile?.email || '',
                      phone: userProfile.bankDetails.phone || userProfile?.phone || userProfile?.contact?.phone || '',
                      businessType: userProfile.bankDetails.businessType || 'individual',
                      businessName: userProfile.bankDetails.businessName || userProfile?.institutionName || userProfile?.coachingName || '',
                      pan: userProfile.bankDetails.pan || userProfile?.pan || '',
                      street1: userProfile.bankDetails.street1 || userProfile?.address?.street || '',
                      street2: userProfile.bankDetails.street2 || userProfile?.address?.street2 || '',
                      city: userProfile.bankDetails.city || userProfile?.address?.city || '',
                      state: userProfile.bankDetails.state || userProfile?.address?.state || '',
                      pincode: userProfile.bankDetails.pincode || userProfile?.address?.pincode || ''
                    });
                  }
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBankDetails}
                disabled={savingBankDetails}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <Save size={16} />
                <span>{savingBankDetails ? 'Saving...' : 'Save Details'}</span>
              </button>
            </div>
          </div>
        )}

        {!showBankDetails && bankDetails.accountNumber && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">Account:</span> {bankDetails.accountNumber.slice(-4).padStart(bankDetails.accountNumber.length, 'X')} | 
              <span className="font-medium ml-2">IFSC:</span> {bankDetails.ifscCode} | 
              <span className="font-medium ml-2">Bank:</span> {bankDetails.bankName}
            </p>
          </div>
        )}
      </motion.div>
      )}

      {/* Business Intelligence Dashboard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {userProfile?.coachingId ? (
          <BusinessIntelligenceDashboard 
            coachingId={userProfile.coachingId}
            timeRange="30"
          />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <p className="text-gray-600 dark:text-gray-400">
                No coaching ID found. Please contact support.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                User ID: {currentUser?.uid}
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Delete Account Section (Only for Coaching Admin) */}
      {userProfile?.role !== 'coaching_staff' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800 mt-8"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <AlertTriangle className="text-red-600 dark:text-red-400" size={24} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Account
                </h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Permanently delete your coaching account. This will delete your account, profile, exams, batches, resources, tests, 
                institution profile, and all admission-related data. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <Trash2 size={18} />
                <span>{deleting ? 'Deleting...' : 'Delete Account'}</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Account Deletion
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete your coaching account? This action cannot be undone.
              <br /><br />
              <strong>This will:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Delete your account and profile</li>
                <li>Delete all your exams</li>
                <li>Delete all your batches</li>
                <li>Delete all your resources</li>
                <li>Delete all your tests</li>
                <li>Delete your institution profile</li>
                <li>Delete all admission applications</li>
                <li>Delete all admission-related data (curriculum, materials, faculty, customizations)</li>
                <li>Delete all admission payments and refunds</li>
              </ul>
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Send Notification Modal */}
      <SendNotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        coachingId={userProfile?.coachingId}
      />
    </div>
  );
};

export default CoachingAdminDashboard;

